import { DateTime, DurationUnits } from 'luxon'

import { DateTimeValue } from '../data-objects'
import { DateTimeFieldAccuracy, DateTimeFieldFormat } from '../definitions'
import { readUserData } from './metadata-storage'

export type DatePrecision = DurationUnits

/**
 * Converts a date string to a DateTime object based on the presence of a timezone data.
 * String dates without timezone information are set on the BU timezone without conversion.
 * String dates with timezone information are converted to the BU timezone.
 * @param date - The date string to convert.
 * @param behaviour - The behavior for handling the DateTime field.
 * @returns The converted DateTime object.
 */
export function getDateFromString(date: string): DateTime {
  const timezone = readUserData()?.business_unit_timezone ?? undefined
  const lang = readUserData()?.lang as string

  let dt = DateTime.fromISO(date).setLocale(lang)

  if (!date.includes('Z')) {
    dt = dt.setZone(timezone, { keepLocalTime: true })
  } else {
    dt = dt.setZone(timezone)
  }

  return dt
}

/**
 * Sets the timezone of a DateTime object based on the specified behaviour.
 * This must be used when the timezone of the DateTime object must be normalized in respect to the browser one.
 * @param date - The DateTime object to set the timezone for.
 * @param behaviour - The behaviour that determines the timezone setting.
 * @returns A new DateTime object with the timezone set according to the behaviour.
 */
export function getDateWithNormalizedTimezone(date: DateTime): DateTime {
  const timezone = readUserData()?.business_unit_timezone ?? undefined
  const lang = readUserData()?.lang as string
  return date.setZone(timezone, { keepLocalTime: true }).setLocale(lang)
}

export function getDateFromJSDate(date: Date): DateTime {
  const timezone = readUserData()?.business_unit_timezone ?? undefined
  const lang = readUserData()?.lang as string
  const newDate = DateTime.fromJSDate(date, { zone: timezone })
  return newDate.setLocale(lang)
}

export function getCurrentDate(): DateTime {
  const zone = readUserData()?.business_unit_timezone ?? undefined
  const lang = readUserData()?.lang as string
  const newDate = DateTime.fromJSDate(new Date(), { zone })
  return newDate.setLocale(lang)
}

export function getToday(): DateTime {
  const zone = readUserData()?.business_unit_timezone ?? undefined
  const lang = readUserData()?.lang as string
  return DateTime.fromJSDate(new Date(), { zone }).startOf('day').setLocale(lang)
}

export function getStringDateFromDateTime(d: DateTime): string {
  return d.toISO() as string
}

export function getJSDateFromDateTime(d: DateTime): Date {
  return d.toJSDate()
}

function getStringFromDateTimeFormat(date: DateTime, format?: DateTimeFieldFormat) {
  switch (format) {
    case DateTimeFieldFormat.g:
      return date.toLocaleString({
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })
    case DateTimeFieldFormat.t:
      return date.toLocaleString({
        hour: 'numeric',
        minute: 'numeric',
      })
    default:
      throw new Error('💥 Date format not supported')
  }
}

function getStringFromDateTimeAccuracy(date: DateTime, accuracy?: DateTimeFieldAccuracy) {
  switch (accuracy) {
    case DateTimeFieldAccuracy.Date:
      return date.toLocaleString({
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
    case DateTimeFieldAccuracy.DateTime:
      return date.toLocaleString({
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      })
    case DateTimeFieldAccuracy.Unknown:
      return date.toUnixInteger().toString()
    case DateTimeFieldAccuracy.Year:
      return date.toLocaleString({
        year: 'numeric',
      })
    case DateTimeFieldAccuracy.YearMonth:
      return date.toLocaleString({
        year: 'numeric',
        month: '2-digit',
      })
    default:
      throw new Error('💥 Date accuracy not supported')
  }
}

type GetStringFromDateTimeOptions = {
  accuracy?: DateTimeFieldAccuracy
  format?: DateTimeFieldFormat
}

export function getStringFromDateTime(date: DateTime, options: GetStringFromDateTimeOptions = {}): string {
  const { format, accuracy } = options
  return format ? getStringFromDateTimeFormat(date, format) : getStringFromDateTimeAccuracy(date, accuracy)
}

export function dateDiff(precision: DatePrecision, a: DateTime, b: DateTime): number {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const diff: any = a.diff(b, precision).toObject()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return diff[precision as any] as number
}

/**
 * Calculates the number of elapsed days between a given date and the current date in UTC.
 * @param date - The date string in ISO format.
 * @returns The number of elapsed days.
 */
export const getElapsedDaysFromDate = (date: string): number => {
  const currentDate = DateTime.utc()
  const startDate = DateTime.fromISO(date, { zone: 'utc' })

  const isSameDay = startDate.hasSame(currentDate, 'day')

  if (isSameDay) {
    return 0
  }

  const isDayBefore = startDate.plus({ days: 1 }).hasSame(currentDate, 'day')

  if (isDayBefore) {
    return 1
  }

  const elapsedDays = Math.floor(currentDate.diff(startDate).as('days'))

  return elapsedDays
}

/**
 * Normalizes a date value to a `DateTime` object.
 *
 * This function accepts a date in various formats (JavaScript `Date`, `DateTime`, or `string`)
 * and returns a `DateTime` object with a normalized timezone. If the input is a JavaScript `Date`,
 * it converts it to a `DateTime` object. If the input is already a `DateTime` object, it normalizes
 * the timezone to avoid using the browser's timezone. If the input is a string, it parses it into
 * a `DateTime` object. If no valid date is provided, it returns undefined.
 *
 * @param date - The date value to be normalized. It can be a JavaScript `Date`, `DateTime`, or `string`.
 * @returns A `DateTime` object with a normalized timezone, or undefined if the input is invalid.
 */
export function getNormalizedDateValue(date?: Date | DateTime | string): DateTime | undefined {
  if (date instanceof Date) {
    return getDateFromJSDate(date)
  } else if (date instanceof DateTime) {
    return getDateWithNormalizedTimezone(date)
  } else if (date) {
    return getDateFromString(date)
  }
  return undefined
}

export const dateTimeToHourMinuteString = (value: DateTimeValue, lang?: string): string => {
  if (!value?.value) return ''

  const dateTime = value.value as DateTime

  if (lang) {
    return dateTime.setLocale(lang).toLocaleString(DateTime.TIME_SIMPLE)
  }
  return dateTime.toFormat('HH:mm')
}
